import * as React from 'react';
import { useCallback, useState } from 'react';
import { Box, Checkbox, Collapse, Grid, IconButton, InputAdornment, List, ListItem, ListItemIcon, ListItemProps, ListItemText, Menu, MenuItem, Stack, TextField, ToggleButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import StraightenIcon from '@mui/icons-material/Straighten';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import NumbersIcon from '@mui/icons-material/Numbers';
import TitleIcon from '@mui/icons-material/Title';
import UndoIcon from '@mui/icons-material/Undo';
import { DragDropContext, Draggable, DropResult, DroppableProvided, DroppableStateSnapshot, DraggableProvided, DraggableStateSnapshot, DraggableRubric } from 'react-beautiful-dnd';
import StrictModeDroppable from './StrictModeDroppable';
import { Field, FieldType, ParentField, } from '../../models/core';
import { AutocompleteField } from '../fields';
import { Check, KeyboardArrowDown, KeyboardArrowUp, Save } from '@mui/icons-material';

type ItemAction = 'up' | 'down' | 'remove';

interface FieldsListItemProps extends ListItemProps {
    field: Field;
    actionClickId: string;
    onTypeClick?: React.MouseEventHandler<HTMLElement>;
    onActionClick?: (id: string, action: ItemAction) => void;
    onEnumItemsChange?: (id: string, value: string) => void;
    onFieldsChanged?:(id: string, value: Field) => void;
}
const FieldsListItem = ({
    field:_field,
    actionClickId,
    onTypeClick,
    onActionClick,
    onEnumItemsChange,
    onFieldsChanged,
    ...props
}: FieldsListItemProps): JSX.Element => {
    const [localField, setLocalField] = useState<Field>(_field || {} as Field);
    const [changed, setChanged] = useState(false);

    React.useEffect(() => {
        setLocalField(_field);
    },[_field]);

    const [enumItems, setEnumItems] = useState((_field.items||[]).join('|'));
    const [collapsed, setCollapsed] = useState(true);

    function onToggleColapsed() { setCollapsed((state) => !state); }

    const setFieldValue = useCallback((key: keyof Field, value: string) => {
        setChanged(true);
        setLocalField(item => ({
            ...item,
            [key]: value
        }));
    }, []);

    const onItemsChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setChanged(true);
        setEnumItems(event.target.value);
    }, []);

    const callActionClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        const attr = event.currentTarget.attributes.getNamedItem('x-action');
        attr && onActionClick && onActionClick(actionClickId, attr.value as ItemAction);
    }, [actionClickId, onActionClick]);

    const callEnabledChange = useCallback((_event: React.ChangeEvent<HTMLInputElement>, checked: boolean, state:Field) => {
        const val = { ...state, 'state': checked ? 'enabled' : 'disabled' } as Field;
        setLocalField(val);
        setChanged(true);
    }, []);

    const callforChild = useCallback((state:Field) => {
        const val = { ...state, 'forChild': !(state as ParentField).forChild } as Field;
        setLocalField(val);
        setChanged(true);
    }, []);

    const onUpdate = useCallback(() => {
        onFieldsChanged && onFieldsChanged(actionClickId, localField);
        onEnumItemsChange && onEnumItemsChange(actionClickId, enumItems);
        setChanged(false);
    }, [actionClickId, enumItems, localField, onEnumItemsChange, onFieldsChanged]);

    return <ListItem {...props} >
        <ListItemIcon sx={{ minWidth: 0 }}><DragIndicatorIcon /></ListItemIcon>
        <Stack direction={'column'} sx={{width:'100%'}}>
        <Grid container sx={{minHeight:80}} spacing={2}>
            <Grid item xs={1}>
            {
                localField.state == 'required'
                    ? <Box sx={{width:'40px', height:'40px'}} />
                    : localField.defaultLabel
                        ? <Checkbox sx={{width:'40px', height:'40px'}}
                            checked={localField.state == 'enabled'} title='Use field' onChange={
                                (i, m) => callEnabledChange(i,m,localField)
                            } />
                        : <IconButton sx={{width:'40px', height:'40px'}}
                            onClick={callActionClick}
                            title='Remove'
                            x-action='remove'
                        ><ClearIcon /></IconButton>
            }
            </Grid>

            <Grid item xs={1}>
            {
                localField.type
                    ? <IconButton sx={{ width:'40px', height:'40px', borderRadius: 1 }} 
                        title='Field Type' onClick={onTypeClick}>
                        {{
                            string: <TitleIcon />,
                            number: <NumbersIcon />,
                            boolean: <CheckIcon />,
                            date: <CalendarMonthIcon />,
                            enum: <FormatListBulletedIcon />,
                            autocomplete: <FormatListBulletedIcon />
                        }[localField.type]}
                        <ArrowDropDownIcon />
                    </IconButton>
                    : <Box sx={{ width:'40px', height:'40px'}} />
            }
            </Grid>
            <Grid item xs={4}>
            {
                <AutocompleteField value={localField.field||''}
                    options={['Some Field', 'Some Field 2']} 
                    name='Field Name' 
                    onChange={(label, values) => console.log('nope')} />
            }
            </Grid>

            <Grid item xs={5}>
            <TextField
                variant='standard'
                label='Label'
                placeholder={localField.defaultLabel}
                value={localField.label}
                disabled={localField.state == 'disabled'}
                onChange={(it) => setFieldValue('label', it.target.value)}
                fullWidth={true}
                InputProps={localField.type ? undefined : {
                    endAdornment: <InputAdornment position='end' sx={{ 
                        visibility: (localField.label && localField.label != localField.defaultLabel) ? 'visible' : 'hidden' 
                        }}>
                        <IconButton sx={{ borderRadius: 1 }} title='Restore Default' onClick={
                            () => setFieldValue('label', localField.defaultLabel||'')
                            }>
                            <UndoIcon fontSize='small' />
                        </IconButton>
                    </InputAdornment>
                }}
            />
            </Grid>
            <Grid item xs={0.5}>
                <IconButton onClick={onToggleColapsed}>
                    {collapsed ? <KeyboardArrowDown/> : <KeyboardArrowUp/> }
                </IconButton>
            </Grid>
            <Grid item xs={0.5}>
                <IconButton onClick={onUpdate} sx={{color:changed ? 'red' : 'green'}}>
                    { changed ? <Save/> : <Check/> }
                </IconButton>
            </Grid>
        </Grid>

        <Collapse in={!collapsed} timeout="auto">
            <Grid container sx={{minHeight:60}} spacing={2}>
                <Grid item xs={5.5}>
                    <TextField
                        label="Default Label"
                        variant='standard'
                        placeholder={localField.defaultLabel}
                        value={localField.defaultLabel}
                        disabled={localField.state == 'disabled'}
                        onChange={(it) => setFieldValue('defaultLabel', it.target.value)}
                        fullWidth={true}
                    />
                </Grid>
                <Grid item xs={5.5}>
                    <TextField
                        label="Clear Label"
                        variant='standard'
                        placeholder={localField.clearLabel}
                        value={localField.clearLabel}
                        disabled={localField.state == 'disabled'}
                        onChange={(it) => setFieldValue('clearLabel', it.target.value)}
                        fullWidth={true}
                    />
                </Grid>
                <Grid item xs={1}>
                    <Box sx={{ flexGrow: 1 }} />
                    <ToggleButton
                        value='check'
                        color='primary'
                        title='Use in Measures sheet'
                        selected={(localField as ParentField)?.forChild}
                        disabled={localField.state == 'disabled'}
                        sx={{ border: 'none', padding: '0 6px', '&.Mui-disabled': { border: 'none' } }}
                        onChange={() => callforChild(localField)}>
                        <StraightenIcon fontSize='small' />
                    </ToggleButton>
                </Grid>

                <Grid item xs={6}>
                    <TextField
                        label="Section"
                        variant='standard'
                        placeholder={localField.section}
                        value={localField.section}
                        disabled={localField.state == 'disabled'}
                        onChange={(it) => setFieldValue('section', it.target.value)}
                        fullWidth={true}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Sub-Section"
                        variant='standard'
                        placeholder={localField.subSection}
                        value={localField.subSection}
                        disabled={localField.state == 'disabled'}
                        onChange={(it) => setFieldValue('subSection', it.target.value)}
                        fullWidth={true}
                    />
                </Grid>

                <Grid item xs={12}>
                    {(localField.type == 'enum' || localField.type == 'autocomplete')
                        ? <TextField
                            variant='standard'
                            placeholder='Enum Items'
                            value={enumItems}
                            disabled={localField.state == 'disabled'}
                            onChange={onItemsChange}
                            fullWidth={true}
                        />
                        : null
                    }
                </Grid>
            </Grid>
        </Collapse>
        </Stack>
    </ListItem>
}

interface FieldListProps<T extends Field> {
    fields: T[];
    factory: () => T;
    onChange?: (fields: T[]) => void;
}

const FieldsList = <T extends Field,>({ fields, factory, onChange }: FieldListProps<T>): JSX.Element => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const onActionClick = useCallback((id: string, action: ItemAction) => {
        const index = fields.findIndex(f => f.id == id);
        switch (action) {
            case 'up':
                onChange && onChange(fields.map((f, i) => i == index ? fields[i - 1] : (i == index - 1 ? fields[i + 1] : f)))
                break;

            case 'down':
                onChange && onChange(fields.map((f, i) => i == index ? fields[i + 1] : (i == index + 1 ? fields[i - 1] : f)))
                break;

            case 'remove':
                onChange && onChange(fields.filter((_, i) => i != index))
                break;
        }
    }, [fields, onChange]);

    const onEnumItemsChange = useCallback((id: string, value: string) => {
        onChange && onChange(fields.map(f => f.id == id ? { ...f, items: value.split('|') } : f))
    }, [fields, onChange]);

    const getAttr = useCallback((el: HTMLElement, name: string): Attr | null => {
        const attr = el.attributes.getNamedItem(name);
        return attr || el.parentElement && getAttr(el.parentElement, name);
    }, []);

    const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        const typeAttr = event.currentTarget.attributes.getNamedItem('x-value');
        const idAttr = anchorEl && getAttr(anchorEl, 'x-id');
        typeAttr && idAttr && onChange
            && onChange(fields.map((f) => (
                f.id == idAttr.value
                    ? { ...f, type: typeAttr.value as FieldType }
                    : f
            )));
        setAnchorEl(null);
    }, [anchorEl, fields, getAttr, onChange]);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const onFieldTypeClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const onDragEnd = useCallback((result: DropResult) => {
        if (onChange && result.destination) {
            const list = Array.from(fields);
            const [removed] = list.splice(result.source.index, 1);
            list.splice(result.destination.index, 0, removed);
            onChange(list);
        }
    }, [onChange, fields]);

    const onAddFieldClick = useCallback(() => {
        onChange && onChange([...fields, factory()])
    }, [fields, factory, onChange]);

    const onFieldsChanged = useCallback((id: string, value: Field) => {
        onChange && onChange(fields.map(f => f.id == id ? value as T : f))
    }, [fields, onChange]);

    return (
        <>
            <DragDropContext onDragEnd={onDragEnd}>
                <StrictModeDroppable droppableId='list'>
                    {(provided: DroppableProvided, _snapshot: DroppableStateSnapshot) => (

                        <List dense {...provided.droppableProps} ref={provided.innerRef} sx={{ pb: 0 }}>
                            {fields.map((field, index) => (
                                <Draggable
                                    key={field.id} draggableId={field.id} index={index}>
                                    {(provided: DraggableProvided, _snapshot: DraggableStateSnapshot, _rubric: DraggableRubric) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                        >
                                            <FieldsListItem
                                                field={field}
                                                actionClickId={field.id}
                                                onActionClick={onActionClick}
                                                onTypeClick={onFieldTypeClick}
                                                onEnumItemsChange={onEnumItemsChange}
                                                onFieldsChanged={onFieldsChanged}
                                                x-id={field.id}
                                            />
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </List>
                    )}
                </StrictModeDroppable>
            </DragDropContext>

            <Box display='flex' justifyContent='end' paddingRight='18px'>
                <IconButton color='primary' title='Add field' onClick={onAddFieldClick}>
                    <AddIcon />
                </IconButton>
            </Box>

            <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose} >
                <MenuItem x-value='string' onClick={handleClick}>
                    <ListItemIcon><TitleIcon /></ListItemIcon>
                    <ListItemText>Text</ListItemText>
                </MenuItem>
                <MenuItem x-value='number' onClick={handleClick}>
                    <ListItemIcon><NumbersIcon /></ListItemIcon>
                    <ListItemText>Number</ListItemText>
                </MenuItem>
                <MenuItem x-value='boolean' onClick={handleClick}>
                    <ListItemIcon><CheckIcon /></ListItemIcon>
                    <ListItemText>Boolean</ListItemText>
                </MenuItem>
                <MenuItem x-value='date' onClick={handleClick}>
                    <ListItemIcon><CalendarMonthIcon /></ListItemIcon>
                    <ListItemText>Date</ListItemText>
                </MenuItem>
                <MenuItem x-value='enum' onClick={handleClick}>
                    <ListItemIcon><FormatListBulletedIcon /></ListItemIcon>
                    <ListItemText>Enum</ListItemText>
                </MenuItem>
                <MenuItem x-value='autocomplete' onClick={handleClick}>
                    <ListItemIcon><FormatListBulletedIcon /></ListItemIcon>
                    <ListItemText>Autocomplete</ListItemText>
                </MenuItem>
            </Menu>
        </>
    )
}

export const PatientFieldsList = React.memo(FieldsList<ParentField>);
export const MeasureFieldsList = React.memo(FieldsList<Field>);

